import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { Role, RoleManagementState, RoleManagementActions } from '../types/role';
import { roleService } from '../services/roleService';

// Initial state
const initialState: RoleManagementState = {
  roles: [],
  isLoading: false,
  error: null
};

// Action types
type RoleAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_ROLES'; payload: Role[] }
  | { type: 'ADD_ROLE'; payload: Role }
  | { type: 'UPDATE_ROLE'; payload: Role }
  | { type: 'DELETE_ROLE'; payload: string }
  | { type: 'INCREMENT_USAGE'; payload: string };

// Reducer
const roleReducer = (state: RoleManagementState, action: RoleAction): RoleManagementState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_ROLES':
      return { ...state, roles: action.payload };
    case 'ADD_ROLE':
      return { ...state, roles: [...state.roles, action.payload] };
    case 'UPDATE_ROLE':
      return {
        ...state,
        roles: state.roles.map(role =>
          role.id === action.payload.id ? action.payload : role
        )
      };
    case 'DELETE_ROLE':
      return {
        ...state,
        roles: state.roles.filter(role => role.id !== action.payload)
      };
    case 'INCREMENT_USAGE':
      return {
        ...state,
        roles: state.roles.map(role =>
          role.name.toLowerCase() === action.payload.toLowerCase()
            ? { ...role, usageCount: role.usageCount + 1 }
            : role
        )
      };
    default:
      return state;
  }
};

// Context
const RoleContext = createContext<RoleManagementState & RoleManagementActions | undefined>(undefined);

// Provider component
interface RoleProviderProps {
  children: ReactNode;
}

export const RoleProvider: React.FC<RoleProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(roleReducer, initialState);

  // Load roles on mount
  useEffect(() => {
    getRoles();
  }, []);

  const getRoles = async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    
    try {
      const roles = await roleService.getRoles();
      dispatch({ type: 'SET_ROLES', payload: roles });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to load roles' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const addRole = async (name: string, description?: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    
    try {
      const newRole = await roleService.addRole(name, description);
      dispatch({ type: 'ADD_ROLE', payload: newRole });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to add role' });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const updateRole = async (id: string, updates: Partial<Role>) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    
    try {
      const updatedRole = await roleService.updateRole(id, updates);
      dispatch({ type: 'UPDATE_ROLE', payload: updatedRole });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to update role' });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const deleteRole = async (id: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    
    try {
      await roleService.deleteRole(id);
      dispatch({ type: 'DELETE_ROLE', payload: id });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to delete role' });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const incrementUsage = (roleName: string) => {
    dispatch({ type: 'INCREMENT_USAGE', payload: roleName });
    // Also update in service
    roleService.incrementUsage(roleName);
  };

  const value = {
    ...state,
    addRole,
    updateRole,
    deleteRole,
    getRoles,
    incrementUsage
  };

  return (
    <RoleContext.Provider value={value}>
      {children}
    </RoleContext.Provider>
  );
};

// Hook to use the role context
export const useRoleManagement = () => {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error('useRoleManagement must be used within a RoleProvider');
  }
  return context;
};
